module Client.Components.MeterReadingsForm

open Client
open Client.Services
open Shared
open global.Elmish
open Fable.Core.JsInterop
open System
open Thoth.Fetch
open Thoth.Json
open Fetch.Types
open Feliz
open Feliz.MaterialUI
open Fable.MaterialUI.Icons
open Fable.MaterialUI.MaterialDesignIcons
open Feliz.SweetAlert
open Feliz.UseElmish
open Fable.Core.Experimental
open Fable.Core.JS
open FsToolkit.ErrorHandling
open CustomerDexImaging.Models

let strOption (s : string) = if String.IsNullOrWhiteSpace s then None else Some s

let parseInt (s : string) =
  if String.IsNullOrWhiteSpace s
  then None
  else
    match Int32.TryParse s with
    | false, _ -> None
    | true, v -> if v > 0 then Some v else None

type FieldState =
  { Validate : bool
    FieldValue : string option
    FieldWarning : string option
    InitialValue : string option } with
    static member defaultValue =
      { Validate = false; InitialValue = None; FieldValue = None; FieldWarning = None }
    static member init v =
      { Validate = false; InitialValue = v; FieldValue = v; FieldWarning = None }
    member this.setValue v =
      { this with Validate = true; FieldValue = v; FieldWarning = if v.IsNone then Some "A value is required." else None }
    member this.getValue () =
      this.FieldValue |> Result.requireSome "A value is required."

type EntryStateDetail =
  { Id : System.Guid
    MeterType : MeterTypeForm
    Reading : int option } with
    static member init meterType =
      { Id = System.Guid.NewGuid()
        MeterType = meterType
        Reading = None }
    static member defaultValue () =
      { Id = System.Guid.NewGuid()
        MeterType = MeterTypeForm.Mono
        Reading = None }

type EntryState =
  { FirstAndLastName : FieldState
    CompanyName : FieldState
    ServiceAddress : FieldState
    EmailAddress : FieldState
    SendConfirmationEmail : bool
    BusinessPhone : FieldState
    EquipmentId : FieldState
    Make : FieldState
    Model : FieldState
    MeterReadings : EntryStateDetail list
    FailureMessage : string option } with
    member this.validate () =
      result {
        let! firstAndLastName = this.FirstAndLastName.getValue ()
        let! companyName = this.CompanyName.FieldValue |> Result.requireSome "A value is required."
        let! serviceAddress = this.ServiceAddress.FieldValue |> Result.requireSome "A value is required."
        let! emailAddress = this.EmailAddress.FieldValue |> Result.requireSome "A value is required."
        let! businessPhone = this.BusinessPhone.FieldValue |> Result.requireSome "A value is required."
        let! equipmentId = this.EquipmentId.FieldValue |> Result.requireSome "A value is required"
        let! make = this.Make.FieldValue |> Result.requireSome "A value is required"
        let! model = this.Model.FieldValue |> Result.requireSome "A value is required"
        let! readings =
          match this.MeterReadings with
          | [] -> Error "At least one reading is required."
          | _ ->
            this.MeterReadings
            |> List.map (fun x ->
              result {
                let! reading = x.Reading |> Result.requireSome "A value is required"
                let d =
                  { MeterReadingDetailFormRequest.MeterType = x.MeterType
                    Reading = reading }
                return d
              }
            )
            |> List.sequenceResultM
        let validatedEntry =
          { MeterReadingFormRequest.FirstAndLastName = firstAndLastName
            CompanyName = companyName
            ServiceAddress = serviceAddress
            EmailAddress = emailAddress
            SendConfirmationEmail = this.SendConfirmationEmail
            BusinessPhone = businessPhone
            EquipmentId = equipmentId
            Make = make
            Model = model
            MeterReadings = readings
            RecaptchaResponse = ""
            FailureMessage = this.FailureMessage
          }

        return validatedEntry
      }
    member this.isValid () =
      match this.validate() with
      | Ok _ -> true
      | Error _ -> false
    static member defaultValue () =
      { FirstAndLastName = FieldState.defaultValue
        CompanyName = FieldState.defaultValue
        ServiceAddress = FieldState.defaultValue
        EmailAddress = FieldState.defaultValue
        SendConfirmationEmail = true
        BusinessPhone = FieldState.defaultValue
        EquipmentId = FieldState.defaultValue
        Make = FieldState.defaultValue
        Model = FieldState.defaultValue
        MeterReadings = [ EntryStateDetail.defaultValue () ]
        FailureMessage = None }
    static member init (equipmentId : string) (failureMessage : string) =
      { (EntryState.defaultValue ()) with EquipmentId = FieldState.init (Some equipmentId); FailureMessage = Some failureMessage; }

type Model =
  | Initialized
  | Sending
  | Sent of string

type Msg =
  | SubmitForm of MeterReadingFormRequest
  | SendingResult of Result<string,string>

let submitForm (request : MeterReadingFormRequest) : Promise<string> =
  let decoder = Services.decoder<string>
  Fetch.post<_,string>("/api/MeterReadings/RequestForm", request, decoder = decoder)

let submitFormCmd request =
  Cmd.OfPromise.either submitForm request
    (Ok >> SendingResult)
    (fun x ->
      "Unable to submit request. Please retry" |> Error |> SendingResult)

let init () =
  Initialized, Cmd.none

let update msg model =
  match msg with
  | SubmitForm r ->
    Sending, submitFormCmd r
  | SendingResult r ->
    match r with
    | Ok v -> Sent v, Cmd.none
    | Error msg ->
      let alert =
        Cmd.Swal.fire [
            swal.title "Unable to process request. Please try again."
            // swal.text msg
          ]
      Initialized, alert

let pendingHeaderMessage =
  Mui.grid [
    grid.container true
    grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.children [
          Mui.typography [
            typography.variant.body1
            typography.children "Please fill out this form to submit meter reading(s)."
          ]
        ]
      ]
    ]
  ]

let detailEntryView setState (model : EntryState) =
  Html.div [
    for item in model.MeterReadings do
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.textField [
                textField.placeholder (sprintf "New %s meter reading" item.MeterType.toString)
                textField.value (item.Reading |> Option.map (sprintf "%i") |> Option.defaultValue "")
                textField.variant.outlined
                textField.margin.normal
                textField.type' "number"
                textField.inputProps [ prop.min 0 ]
                textField.fullWidth true
                textField.label (sprintf "New %s meter reading" item.MeterType.toString)
                textField.required true
                textField.onChange (fun (ev : Browser.Types.Event) ->
                  setState { model with MeterReadings = model.MeterReadings |> List.map (fun r -> if r.Id = item.Id then { r with Reading = parseInt (!!ev.target?value) } else r) } )
              ]
            ]
          ]
        ]
      ]

    if model.MeterReadings |> List.exists (fun x -> x.MeterType = MeterTypeForm.Color) |> not then
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.typography [
                typography.variant.h6
                typography.children "Have a color meter?"
              ]
            ]
          ]
        ]
      ]
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.typography [
                typography.variant.body1
                typography.children "If you have a color meter on this equipment, you can add a color reading to your request by pressing the + button."
              ]
            ]
          ]
        ]
      ]
      Mui.grid [
        grid.container true
        grid.item true
        grid.alignContent.center
        grid.alignItems.center
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.xs._1
            grid.children [
              Mui.tooltip [
                tooltip.title ("Add item")
                tooltip.children (
                  Mui.fab [
                    fab.size.medium
                    fab.color.primary
                    fab.children [ plusIcon []]
                    prop.onClick (fun e ->
                      e.preventDefault ()
                      setState { model with MeterReadings = List.append model.MeterReadings [EntryStateDetail.init MeterTypeForm.Color ]}
                    )
                  ]
                )
              ]
            ]
          ]
        ]
      ]
  ]

let view = React.functionComponent (fun (inputProps: {| initialValues : EntryState option; reCaptchaSiteKey : string; returnResult: unit -> unit; |}) ->
  let state, setState = React.useState(inputProps.initialValues |> Option.defaultWith EntryState.defaultValue)
  let model, dispatch = React.useElmish(init (), update, [| |])
  let warningState, setWarningState = React.useState<string option> None
  let classes = Styles.useStyles()
  let effect () = ReCaptcha.initialize inputProps.reCaptchaSiteKey
  Feliz.React.useEffect (effect , [||])
  match model with
  | Sent msg ->
    Html.div [
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.grid [
                grid.item true
                grid.children [
                  Mui.typography [
                    let successMessage =
                      if state.SendConfirmationEmail
                      then "Thank you. Your meter reading(s) have been received, and you will receive an notification when your request has been processed."
                      else "Thank you. Your meter reading(s) have been received."
                    typography.variant.body1
                    typography.children successMessage
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.grid [
                grid.item true
                grid.children [
                  Mui.button [
                    prop.type'.submit
                    button.fullWidth true
                    button.variant.contained
                    button.color.primary
                    button.children "Add Another Reading"
                    prop.onClick (fun e ->
                      e.preventDefault ()
                      inputProps.returnResult()
                    )
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
    ]
  | Sending
  | Initialized ->
    Html.div [
      pendingHeaderMessage
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Html.form [
                prop.children [
                  Mui.textField [
                    textField.placeholder "Enter Your First and Last Name"
                    textField.defaultValue (state.FirstAndLastName.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "First & Last Name"
                    textField.name "FirstAndLastName"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) -> setState { state with FirstAndLastName = strOption !!ev.target?value |> state.FirstAndLastName.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder "Company Name"
                    textField.defaultValue (state.CompanyName.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Company"
                    textField.name "CompanyName"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) -> setState { state with CompanyName = strOption !!ev.target?value |> state.CompanyName.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder ""
                    textField.defaultValue (state.ServiceAddress.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Service Address"
                    textField.name "ServiceAddress"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) -> setState { state with ServiceAddress = strOption !!ev.target?value |> state.ServiceAddress.setValue })
                  ]
                  let emailHandler emailOption = setState { state with EmailAddress = state.EmailAddress.setValue emailOption }
                  Components.EmailAddress.view {| initialValue = state.EmailAddress.InitialValue; allowEntry = true; isRequired = true; returnResult = emailHandler |}

                  Mui.grid [
                    grid.container true
                    //grid.direction.column
                    grid.spacing._2
                    grid.children [
                      Mui.grid [
                        grid.item true
                        grid.xs._6
                        grid.children [
                          Mui.formControl [
                            Mui.formLabel [
                              Mui.formGroup [
                                Mui.formControlLabel [
                                  formControlLabel.control (
                                    Mui.checkbox [
                                      checkbox.checked' state.SendConfirmationEmail
                                      checkbox.onChange (fun (b : bool) -> setState { state with SendConfirmationEmail = b })
                                      prop.name "Send confirmation email"
                                    ]
                                  )
                                  formControlLabel.label "Send confirmation email"
                                ]
                              ]
                            ]
                          ]
                        ]
                      ]
                    ]
                  ]

                  Components.PhoneNumber.view {| initialValue = state.BusinessPhone.InitialValue; allowEntry = true; isRequired = true; returnResult = (fun v -> setState { state with BusinessPhone = state.BusinessPhone.setValue v }) |}
                  Mui.textField [
                    textField.placeholder "Equipment ID (Asset Number)"
                    textField.defaultValue (state.EquipmentId.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Equipment ID"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) ->
                      setState { state with EquipmentId = strOption !!ev.target?value |> state.EquipmentId.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder "Equipment Make"
                    textField.defaultValue (state.Make.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Make"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) ->
                      setState { state with Make = strOption !!ev.target?value |> state.Make.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder "Equipment Model"
                    textField.defaultValue (state.Model.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Model"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) ->
                      setState { state with Model = strOption !!ev.target?value |> state.Model.setValue })
                  ]
                  detailEntryView setState state

                  let buttonProgressIndicator =
                    Mui.circularProgress [
                      prop.className classes.buttonProgress
                      prop.size 24
                    ]


                  Mui.grid [
                    grid.container true
                    grid.spacing._2
                    grid.children [
                      Mui.grid [
                        grid.item true
                        grid.xs._6
                        grid.children [
                          Html.div [
                            prop.className classes.wrapper
                            prop.children [
                              Mui.button [
                                prop.type'.submit
                                button.fullWidth true
                                button.variant.contained
                                button.color.primary
                                button.children "Submit"
                                button.disabled (state.isValid() |> not || model = Sending)
                                prop.onClick (fun e ->
                                  e.preventDefault ()
                                  match state.validate() with
                                  | Ok v ->
                                    // inputProps.returnResult () // (Some v)
                                    let fn token =
                                      SubmitForm { v with RecaptchaResponse = token; } |> dispatch
                                    ReCaptcha.registerCallBack inputProps.reCaptchaSiteKey Constants.recaptchaSubmitMeterReadingsAction fn
                                  | Error msg -> setWarningState (Some msg)
                                )
                              ]
                              if model = Sending then buttonProgressIndicator else Html.none
                            ]
                          ]
                        ]
                      ]
                      Mui.grid [
                        grid.item true
                        grid.xs._6
                        grid.children [
                          Mui.button [
                            button.fullWidth true
                            button.variant.contained
                            button.color.default'
                            button.children "Cancel"
                            prop.onClick (fun e ->
                              e.preventDefault ()
                              inputProps.returnResult()
                            )
                          ]
                        ]
                      ]
                      if warningState.IsSome then Html.text warningState.Value else Html.none
                    ]
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
    ]
)